import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Card } from 'primereact/card'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Calendar } from 'primereact/calendar'
import { Dropdown } from 'primereact/dropdown'
import { Checkbox } from 'primereact/checkbox'
import { Link, useNavigate } from 'react-router-dom'

const registrationSchema = z.object({
  firstName: z.string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters long')
    .max(50, 'First name cannot exceed 50 characters')
    .regex(/^[a-zA-ZäöüÄÖÜß\s-']+$/, 'First name can only contain letters, spaces, hyphens, and apostrophes'),
  lastName: z.string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters long')
    .max(50, 'Last name cannot exceed 50 characters')
    .regex(/^[a-zA-ZäöüÄÖÜß\s-']+$/, 'Last name can only contain letters, spaces, hyphens, and apostrophes'),
  email: z.string()
    .min(1, 'Email address is required')
    .email('Please enter a valid email address (e.g., user@example.com)')
    .max(255, 'Email address cannot exceed 255 characters')
    .toLowerCase(),
  phone: z.string()
    .optional()
    .refine(val => !val || val.length >= 8, 'Phone number must be at least 8 digits long')
    .refine(val => !val || /^[\d\s+()-]+$/.test(val), 'Phone number can only contain digits, spaces, parentheses, hyphens, and plus signs'),
  dateOfBirth: z.date()
    .optional()
    .refine(val => !val || val <= new Date(), 'Date of birth cannot be in the future')
    .refine(val => !val || val >= new Date('1900-01-01'), 'Please enter a valid date of birth'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY']).optional(),
  earnsIncome: z.boolean().default(false),
  address: z.object({
    street1: z.string().optional(),
    street2: z.string().optional(),
    city: z.string().optional(),
    zipCode: z.string()
      .optional()
      .refine(val => !val || /^\d{5}$/.test(val), 'ZIP code must be 5 digits'),
    country: z.string().optional()
  }).optional(),
  captchaText: z.string()
    .min(1, 'CAPTCHA verification is required')
    .min(4, 'CAPTCHA text must be at least 4 characters')
    .max(10, 'CAPTCHA text cannot exceed 10 characters')
})

type RegistrationFormData = z.infer<typeof registrationSchema>

interface CaptchaData {
  sessionId: string
  imageUrl: string
}

const genderOptions = [
  { label: 'Male', value: 'MALE' },
  { label: 'Female', value: 'FEMALE' },
  { label: 'Other', value: 'OTHER' },
  { label: 'Prefer not to say', value: 'PREFER_NOT_TO_SAY' }
]

const MemberRegistrationForm: React.FC = () => {
  const navigate = useNavigate()
  const [error, setError] = useState<string>('')
  const [captcha, setCaptcha] = useState<CaptchaData | null>(null)
  const [captchaLoading, setCaptchaLoading] = useState(false)

  const {
    control,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<RegistrationFormData>({
    resolver: zodResolver(registrationSchema),
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dateOfBirth: new Date(new Date().setFullYear(new Date().getFullYear() - 20)),
      gender: undefined,
      earnsIncome: false,
      address: {
        street1: '',
        street2: '',
        city: '',
        zipCode: '',
        country: 'Germany'
      },
      captchaText: ''
    }
  })

  const loadCaptcha = async () => {
    try {
      setCaptchaLoading(true)
      const response = await fetch('/api/public/captcha')

      if (!response.ok) {
        throw new Error('Failed to load CAPTCHA')
      }

      const sessionId = response.headers.get('X-Captcha-Session')
      if (!sessionId) {
        throw new Error('No CAPTCHA session ID received')
      }

      const svgData = await response.text()
      const imageUrl = `data:image/svg+xml;base64,${btoa(svgData)}`

      setCaptcha({ sessionId, imageUrl })
    } catch (err: any) {
      setError(err.message || 'Failed to load CAPTCHA')
    } finally {
      setCaptchaLoading(false)
    }
  }

  useEffect(() => {
    loadCaptcha()
  }, [])

  const onSubmit = async (data: RegistrationFormData) => {
    try {
      setError('')

      if (!captcha) {
        setError('Please load CAPTCHA first')
        return
      }

      const registrationData = {
        ...data,
        captchaSessionId: captcha.sessionId,
        address: data.address?.street1 ? data.address : undefined,
        earnsIncome: data.earnsIncome
      }

      const response = await fetch('/api/public/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(registrationData)
      })

      const result = await response.json()

      if (!response.ok) {
        // Handle specific error cases
        let errorMessage = result.error?.message || 'Registration failed'

        // Provide more helpful error messages based on error codes
        switch (result.error?.code) {
          case 'MISSING_REQUIRED_FIELDS':
            errorMessage = 'Please fill in all required fields: First name, Last name, and Email address'
            break
          case 'EMAIL_EXISTS':
            errorMessage = 'This email address is already registered. Please use a different email or try logging in.'
            break
          case 'INVALID_CAPTCHA':
            errorMessage = 'The CAPTCHA text you entered is incorrect or has expired. Please try again.'
            break
          case 'CAPTCHA_REQUIRED':
            errorMessage = 'CAPTCHA verification is required. Please complete the CAPTCHA challenge.'
            break
          case 'RATE_LIMIT_EXCEEDED':
            errorMessage = 'Too many registration attempts. Please wait 15 minutes before trying again.'
            break
          case 'CAPTCHA_RATE_LIMIT':
            errorMessage = 'Too many CAPTCHA requests. Please wait 5 minutes before requesting a new CAPTCHA.'
            break
          case 'VALIDATION_ERROR':
            errorMessage = result.error?.message || 'Please check your input data and try again.'
            break
          case 'DUPLICATE_EMAIL':
            errorMessage = 'This email address is already registered. Please use a different email or try logging in.'
            break
          default:
            // Try to extract more details from validation errors
            if (result.error?.details && Array.isArray(result.error.details)) {
              const validationErrors = result.error.details.map((detail: any) => detail.message).join(', ')
              errorMessage = `Validation errors: ${validationErrors}`
            } else if (result.error?.message) {
              errorMessage = result.error.message
            }
        }

        setError(errorMessage)

        // Only refresh CAPTCHA for CAPTCHA-related errors
        if (result.error?.code === 'INVALID_CAPTCHA' || result.error?.code === 'CAPTCHA_REQUIRED') {
          loadCaptcha()
        }
        return
      }

      // Navigate to registration success page with success message
      navigate('/registration-success', {
        state: {
          message: result.message || 'Registration successful! Your membership is pending admin approval - we\'ll review your application and get back to you soon.',
          type: 'success'
        }
      })
    } catch (err: any) {
      // Handle network or parsing errors
      if (err.name === 'TypeError' && err.message.includes('fetch')) {
        setError('Unable to connect to the server. Please check your internet connection and try again.')
      } else {
        setError(err.message || 'An unexpected error occurred. Please try again.')
      }
      // Don't refresh CAPTCHA on network errors
    }
  }

  const refreshCaptcha = () => {
    loadCaptcha()
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Member Registration
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Register to become a member of our organization
          </p>
        </div>

        <Card className="shadow-xl border-0">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-t-lg">
            <h3 className="text-xl font-semibold text-white mb-2">
              Join Our Community
            </h3>
            <p className="text-blue-100 text-sm">
              Fill out the form below to apply for membership
            </p>
          </div>
          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-6" noValidate>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <i className="pi pi-exclamation-triangle text-red-400"></i>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-field">
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name *
                </label>
                <Controller
                  name="firstName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="firstName"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.firstName ? 'p-invalid' : ''}`}
                      placeholder="Enter your first name"
                    />
                  )}
                />
                {errors.firstName && (
                  <small className="text-red-500 mt-1 block">{errors.firstName.message}</small>
                )}
              </div>

              <div className="form-field">
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name *
                </label>
                <Controller
                  name="lastName"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="lastName"
                      value={field.value}
                      onChange={field.onChange}
                      className={`w-full ${errors.lastName ? 'p-invalid' : ''}`}
                      placeholder="Enter your last name"
                    />
                  )}
                />
                {errors.lastName && (
                  <small className="text-red-500 mt-1 block">{errors.lastName.message}</small>
                )}
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <InputText
                    id="email"
                    type="email"
                    value={field.value}
                    onChange={field.onChange}
                    className={`w-full ${errors.email ? 'p-invalid' : ''}`}
                    placeholder="Enter your email address"
                  />
                )}
              />
              {errors.email && (
                <small className="text-red-500 mt-1 block">{errors.email.message}</small>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="form-field">
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Phone Number
                </label>
                <Controller
                  name="phone"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="phone"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className="w-full"
                      placeholder="Enter your phone number"
                    />
                  )}
                />
              </div>

              <div className="form-field">
                <label htmlFor="dateOfBirth" className="block text-sm font-medium text-gray-700 mb-2">
                  Date of Birth
                </label>
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field }) => (
                    <Calendar
                      id="dateOfBirth"
                      value={field.value}
                      onChange={(e) => field.onChange(e.value)}
                      className="w-full"
                      placeholder="Select your date of birth"
                      showIcon
                      maxDate={new Date()}
                      yearRange="1900:2024"
                      dateFormat="dd/mm/yy"
                    />
                  )}
                />
              </div>
            </div>

            <div className="form-field">
              <label htmlFor="gender" className="block text-sm font-medium text-gray-700 mb-2">
                Gender
              </label>
              <Controller
                name="gender"
                control={control}
                render={({ field }) => (
                  <Dropdown
                    id="gender"
                    value={field.value}
                    onChange={(e) => field.onChange(e.value)}
                    options={genderOptions}
                    className="w-full"
                    placeholder="Select your gender"
                  />
                )}
              />
            </div>

            <div className="form-field">
              <div className="flex items-center gap-2">
                <Controller
                  name="earnsIncome"
                  control={control}
                  render={({ field }) => (
                    <Checkbox
                      id="earnsIncome"
                      checked={field.value}
                      onChange={(e) => field.onChange(e.checked)}
                      className="mr-2"
                    />
                  )}
                />
                <label htmlFor="earnsIncome" className="text-sm font-medium text-gray-700">
                  I currently earn income from employment or self-employment
                </label>
              </div>
              <small className="text-gray-500 mt-1 block">
                This information helps us determine appropriate membership fees
              </small>
            </div>

            <div className="space-y-4 border-t pt-6">
              <h3 className="text-lg font-medium text-gray-900 flex items-center">
                <i className="pi pi-map-marker mr-2 text-blue-600"></i>
                Address Information (Optional)
              </h3>

              <div className="form-field">
                <label htmlFor="street1" className="block text-sm font-medium text-gray-700 mb-2">
                  Street Address
                </label>
                <Controller
                  name="address.street1"
                  control={control}
                  render={({ field }) => (
                    <InputText
                      id="street1"
                      value={field.value || ''}
                      onChange={field.onChange}
                      className="w-full"
                      placeholder="Enter your street address"
                    />
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="form-field">
                  <label htmlFor="city" className="block text-sm font-medium text-gray-700 mb-2">
                    City
                  </label>
                  <Controller
                    name="address.city"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id="city"
                        value={field.value || ''}
                        onChange={field.onChange}
                        className="w-full"
                        placeholder="Enter your city"
                      />
                    )}
                  />
                </div>

                <div className="form-field">
                  <label htmlFor="zipCode" className="block text-sm font-medium text-gray-700 mb-2">
                    ZIP Code
                  </label>
                  <Controller
                    name="address.zipCode"
                    control={control}
                    render={({ field }) => (
                      <InputText
                        id="zipCode"
                        value={field.value || ''}
                        onChange={field.onChange}
                        className="w-full"
                        placeholder="Enter your ZIP code"
                      />
                    )}
                  />
                </div>
              </div>
            </div>

            <div className="form-field border-t pt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <i className="pi pi-shield mr-2 text-blue-600"></i>
                Security Verification *
              </label>
              <div className="flex items-center space-x-4 mb-4">
                {captcha ? (
                  <img
                    src={captcha.imageUrl}
                    alt="CAPTCHA"
                    className="border border-gray-300 rounded"
                    style={{ height: '60px' }}
                  />
                ) : (
                  <div className="w-32 h-16 bg-gray-200 border border-gray-300 rounded flex items-center justify-center">
                    {captchaLoading ? (
                      <i className="pi pi-spin pi-spinner" />
                    ) : (
                      <span className="text-gray-500 text-sm">Loading...</span>
                    )}
                  </div>
                )}
                <Button
                  type="button"
                  icon="pi pi-refresh"
                  className="p-button-outlined p-button-sm"
                  onClick={refreshCaptcha}
                  disabled={captchaLoading}
                  tooltip="Refresh CAPTCHA"
                />
              </div>
              <Controller
                name="captchaText"
                control={control}
                render={({ field }) => (
                  <InputText
                    value={field.value}
                    onChange={field.onChange}
                    className={`w-full ${errors.captchaText ? 'p-invalid' : ''}`}
                    placeholder="Enter the text shown in the image above"
                  />
                )}
              />
              {errors.captchaText && (
                <small className="text-red-500 mt-1 block">{errors.captchaText.message}</small>
              )}
            </div>

            <Button
              type="submit"
              label={isSubmitting ? 'Processing Registration...' : 'Complete Registration'}
              icon={isSubmitting ? 'pi pi-spin pi-spinner' : 'pi pi-user-plus'}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 border-0 py-3 text-lg font-semibold transition-all duration-200 transform hover:scale-105 hover:shadow-lg"
              disabled={isSubmitting || !captcha}
            />
          </form>
        </Card>

        <div className="text-center bg-white rounded-lg shadow-sm p-4">
          <p className="text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500 transition-colors duration-200">
              Sign in here
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}

export default MemberRegistrationForm